/* global React */
import type { SyntheticEvent } from 'react'
import type { Theme } from '@mui/material/styles'
import { useStyles } from 'platform/react/hook'
import { type ImageFit, type PictureProps } from 'ui/Element/Image'

/**
 * Returns the picture element's current theme styles.
 *
 * @param theme      the application's current theme
 * @param props       the element's properties
 * @param props.fit   how the image is fitted into its container
 * @param props.inset whether the image is inscribed into a circle
 * @returns           the picture's styles
 */
const styles = (theme: Theme, { fit, inset }: { fit: ImageFit, inset: boolean }) => ({
  image: {
    position: 'absolute',
    display: 'block',
    ...inset
      ? {
        top: '15%',
        left: '15%',
        width: '70%',
        height: '70%',
      }
      : {
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
    objectFit: fit,
    objectPosition: 'center',
  },
})

/**
 * Picture Element
 *
 * Purely presentational element that renders an image source. It always spans
 * its parent container and centers the image inside of it, either letterboxed
 * (`fit="contain"`) or cropped (`fit="cover"`), preserving its aspect ratio.
 *
 * @param props        the element's properties
 * @param props.src    url of the image to display
 * @param props.alt    alternative text of the image
 * @param props.fit    how the image is fitted into its container
 * @param props.inset  whether the image is inscribed into a circle
 * @param props.events event handlers (`onLoad`)
 * @returns            the new picture element
 */
const Picture = ({ src, alt = '', fit = 'contain', inset = false, events = {} }: PictureProps) => {
  const classes = useStyles(styles, { fit, inset })() as Record<string, string>

  return (
    <img
      src={src}
      alt={alt}
      className={classes.image}
      onLoad={(event: SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth: width, naturalHeight: height } = event.currentTarget
        events.onLoad?.({ width, height })
      }}
    />
  )
}

export default Picture
